/**
 * Source contributions for sections whose facts come from more than one
 * provider. `buildSedProviderReferences` cites a verified section's provider
 * contributions instead of its own source whenever any exist, so a section
 * whose primary provider has no contribution of its own drops that source from
 * the report reference appendix as soon as a secondary lookup adds one — the
 * environmental section lost "USFWS Critical Habitat / NWI Wetlands" once the
 * EPA designation or the PAD-US screen contributed.
 */
import type { EvidenceSection } from '../../types/siteEvidence';
import { resolveEvidenceSourcePolicy } from '../../services/evidenceSourcePolicy';

/**
 * Lists the section's own source as a whole-section (`*`) contribution when
 * other provider contributions exist without one. The grade and
 * qualification come from the same policy resolver the reference builder uses
 * for a section without contributions, so the primary reference is unchanged.
 */
export function withPrimaryContribution<T>(section: EvidenceSection<T>): EvidenceSection<T> {
  if (section.data == null || !section.status.startsWith('verified')) return section;
  const contributions = section.sourceContributions ?? [];
  const providerContributions = contributions.filter(contribution => !contribution.documentId);
  if (providerContributions.length === 0 || providerContributions.some(contribution => contribution.path === '*')) {
    return section;
  }
  const policy = resolveEvidenceSourcePolicy(section as EvidenceSection<unknown>);
  return {
    ...section,
    sourceContributions: [{
      path: '*',
      grade: policy.grade,
      qualification: policy.qualification,
      source: section.source,
      ...(section.sourceUrl ? { sourceUrl: section.sourceUrl } : {}),
      retrievedAt: section.retrievedAt,
      ...(section.dataAsOf ? { dataAsOf: section.dataAsOf } : {}),
    }, ...contributions],
  };
}

/**
 * Keeps a previously persisted field that a separate lookup contributes to a
 * section (the EPA designation `naaqsDesignations`, the PAD-US screen
 * `protectedAreas`) when a refresh recompiled the section WITHOUT attempting
 * that lookup: the fresh slot has neither the field nor a contribution at its
 * path. An attempted lookup always records one (its value, or an
 * 'unavailable' contribution when it failed), so a failure is recorded as not
 * assessed and never hidden behind the old value. The backstop to the
 * provider pairing in `providerCompanions.ts`, mirroring the regulatory
 * jurisdiction carry-forward.
 */
export function carryForwardContributedField(
  fresh: EvidenceSection<unknown>,
  existing: EvidenceSection<unknown> | undefined,
  path: string,
): EvidenceSection<unknown> {
  const prior = (existing?.data as Record<string, unknown> | null | undefined)?.[path];
  const freshData = fresh.data as Record<string, unknown> | null | undefined;
  if (prior === undefined || !freshData || freshData[path] !== undefined) return fresh;
  if ((fresh.sourceContributions ?? []).some(contribution => contribution.path === path)) return fresh;
  return withPrimaryContribution({
    ...fresh,
    data: { ...freshData, [path]: prior },
    sourceContributions: [
      ...(fresh.sourceContributions ?? []),
      ...(existing?.sourceContributions ?? []).filter(contribution => contribution.path === path),
    ],
  });
}

/** The separately attributed screens that ride the environmental section. */
const SEPARATE_ENVIRONMENTAL_SCREENS = ['naaqsDesignations', 'protectedAreas'] as const;
/** Habitat and wetlands fields only the USFWS primary lookup produces. */
const HABITAT_FIELDS = [
  'hasCriticalHabitat', 'criticalHabitatFindings', 'criticalHabitatCoverage', 'hasWetlands', 'wetlandType',
  'wetlandAcres', 'wetlandAcreageBasis', 'wetlandSourceAcres', 'wetlandSourceAcreageConflicts',
  'wetlandAcreageIssues', 'wetlandMappingCoverage',
] as const;

function hasHabitatEvidence(data: Record<string, unknown> | null | undefined): boolean {
  if (!data) return false;
  const species = data.species;
  return (Array.isArray(species) && species.length > 0) || HABITAT_FIELDS.some(field => data[field] !== undefined);
}

/**
 * Keeps prior habitat and wetlands evidence when a scoped refresh recompiled
 * the environmental section WITHOUT any USFWS data. A failed habitat lookup
 * beside a successful EPA designation or PAD-US screen compiles an
 * 'anomalous' slot holding only that screen plus `species: []`, so the screen
 * survives on a fresh build; grafting that slot over a section whose habitat
 * and wetlands were verified would erase them (before the screens existed the
 * slot stayed 'unavailable', which a top-up never grafts). The prior slot is
 * kept and only the freshly looked-up screens and their contributions are
 * carried into it.
 */
export function keepPriorHabitatEvidence(
  fresh: EvidenceSection<unknown>,
  existing: EvidenceSection<unknown> | undefined,
): EvidenceSection<unknown> {
  const freshData = fresh.data as Record<string, unknown> | null | undefined;
  const priorData = existing?.data as Record<string, unknown> | null | undefined;
  if (!existing || fresh.status !== 'anomalous' || !freshData || !priorData) return fresh;
  if (hasHabitatEvidence(freshData) || !hasHabitatEvidence(priorData)) return fresh;
  const carried = SEPARATE_ENVIRONMENTAL_SCREENS.filter(path => freshData[path] !== undefined);
  const isCarried = (path: string): boolean => (carried as readonly string[]).includes(path);
  return withPrimaryContribution({
    ...existing,
    data: { ...priorData, ...Object.fromEntries(carried.map(path => [path, freshData[path]])) },
    sourceContributions: [
      ...(existing.sourceContributions ?? []).filter(contribution => !isCarried(contribution.path)),
      ...(fresh.sourceContributions ?? []).filter(contribution => isCarried(contribution.path)),
    ],
  });
}

const NAAQS_PATH = 'naaqsDesignations';
/** Anomaly line a compile writes when the EPA designation lookup fails (`withNaaqsDesignations`). */
const EPA_LOOKUP_ANOMALY = /^EPA NAAQS designation lookup unavailable\b/;

/**
 * F5 — an EPA-only refresh of the environmental section: `topUpSiteSed` runs
 * `epa-attainment` alone when the stored designation predates the shipped
 * Green Book table, or when a caller asks for that provider alone. `fresh` is
 * the section compiled from that EPA-only fetch (no USFWS data, so a
 * screens-only slot). The stored section is kept whole — USFWS habitat and
 * wetlands, the PAD-US, FRS and AIANNH screens, status, amendments — and only
 * the designation and its contribution are swapped in, so the refresh can
 * neither erase habitat evidence nor graft a screens-only slot. A stale
 * "lookup unavailable" note from an earlier compile is cleared.
 *
 * A failed lookup (no designation in `fresh`) changes nothing: `existing`
 * itself is returned. An unprompted vintage refresh must not drop a
 * coverage-backed listing to "not assessed", nor add a note to the hashed
 * section: either would move the scorecard, the environmental content hash
 * and the Stage 2 basis with no new evidence. The caller logs the failure,
 * saves nothing, and retries at the next top-up, since the block is still
 * stale. With no stored section the fresh slot is returned as is.
 */
export function withRefreshedNaaqsDesignations(
  existing: EvidenceSection<unknown> | undefined,
  fresh: EvidenceSection<unknown>,
): EvidenceSection<unknown> {
  const priorData = existing?.data as Record<string, unknown> | null | undefined;
  if (!existing || !priorData || existing.status === 'unavailable') return fresh;
  const designation = (fresh.data as Record<string, unknown> | null | undefined)?.[NAAQS_PATH];
  if (designation === undefined) return existing;
  const freshContributions = (fresh.sourceContributions ?? []).filter(contribution => contribution.path === NAAQS_PATH);
  const otherAnomalies = existing.anomalies.filter(anomaly => !EPA_LOOKUP_ANOMALY.test(anomaly));
  const otherContributions = (existing.sourceContributions ?? []).filter(contribution => contribution.path !== NAAQS_PATH);
  return withPrimaryContribution({
    ...existing,
    data: { ...priorData, [NAAQS_PATH]: designation },
    anomalies: otherAnomalies,
    sourceContributions: [...otherContributions, ...freshContributions],
  });
}
